import {
    Home,
    Workflow,
    AlertTriangle,
    Users,
    Building2,
    Shield,
    CheckCircle2,
    Activity,
    Settings,
    Terminal,
    UsersRound,
    Building,
    User,
    Tag,
    Factory,
    Image,
    Database,
    FileSpreadsheet,
    Code,
    Webhook,
    Copy,
    GitMerge,
    Network,
    Mail,
    LayoutDashboard,
    GitBranch,
    FileText,
    Paperclip,
    FolderOpen,
    Key,
    Monitor,
    History,
    Bell,
    ClipboardCheck,
    FormInput,
} from 'lucide-react';
import type { NavigateFunction } from 'react-router-dom';
import type { GlobalCommand } from '../types';

/**
 * Navigation commands for Global Command Palette
 * Go to different pages in the application
 *
 * Updated: 2026-02-13 - Synced with all router routes (36 commands)
 */
export function navigationCommands(
    navigate: NavigateFunction,
    onClose: (open: boolean) => void
): GlobalCommand[] {
    return [
        // ═══════════════════════════════════════════════════════════════
        // CORE NAVIGATION
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-dashboard',
            title: 'Go to Dashboard',
            shortcut: 'G H',
            icon: LayoutDashboard,
            group: 'navigation',
            action: () => {
                navigate('/dashboard');
                onClose(false);
            },
        },
        {
            id: 'nav-canvas',
            title: 'Go to Canvas',
            shortcut: 'G C',
            icon: Home,
            group: 'navigation',
            action: () => {
                navigate('/canvas');
                onClose(false);
            },
        },
        {
            id: 'nav-visual-flows',
            title: 'Go to Visual Flows',
            shortcut: '',
            icon: GitBranch,
            group: 'navigation',
            action: () => {
                navigate('/visual-flows');
                onClose(false);
            },
        },
        {
            id: 'nav-workflows',
            title: 'Go to Workflow Automation',
            shortcut: 'G W',
            icon: Workflow,
            group: 'navigation',
            action: () => {
                navigate('/workflow-automation');
                onClose(false);
            },
        },
        {
            id: 'nav-incidents',
            title: 'Go to Incidents',
            shortcut: 'G I',
            icon: AlertTriangle,
            group: 'navigation',
            action: () => {
                navigate('/incidents');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // PARTIES & CONTACTS
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-persons',
            title: 'Go to Persons',
            shortcut: 'G P',
            icon: User,
            group: 'navigation',
            action: () => {
                navigate('/persons');
                onClose(false);
            },
        },
        {
            id: 'nav-companies',
            title: 'Go to Companies',
            shortcut: '',
            icon: Building,
            group: 'navigation',
            action: () => {
                navigate('/companies');
                onClose(false);
            },
        },
        {
            id: 'nav-documents',
            title: 'Go to Documents',
            shortcut: '',
            icon: FileText,
            group: 'navigation',
            action: () => {
                navigate('/documents');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // ORGANIZATION & USERS
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-users',
            title: 'Go to Users',
            shortcut: 'G U',
            icon: Users,
            group: 'navigation',
            action: () => {
                navigate('/users');
                onClose(false);
            },
        },
        {
            id: 'nav-organization',
            title: 'Go to Organization',
            shortcut: 'G O',
            icon: Network,
            group: 'navigation',
            action: () => {
                navigate('/organization');
                onClose(false);
            },
        },
        {
            id: 'nav-teams',
            title: 'Go to Teams',
            shortcut: 'G T',
            icon: UsersRound,
            group: 'navigation',
            action: () => {
                navigate('/teams');
                onClose(false);
            },
        },
        {
            id: 'nav-business-units',
            title: 'Go to Business Units',
            shortcut: 'G B',
            icon: Building2,
            group: 'navigation',
            action: () => {
                navigate('/business-units');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // DATA GOVERNANCE & COMPLIANCE
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-compliance',
            title: 'Go to Compliance',
            shortcut: 'G L',
            icon: CheckCircle2,
            group: 'navigation',
            action: () => {
                navigate('/compliance');
                onClose(false);
            },
        },
        {
            id: 'nav-data-classification',
            title: 'Go to Data Classification',
            shortcut: '',
            icon: Database,
            group: 'navigation',
            action: () => {
                navigate('/data-classification');
                onClose(false);
            },
        },
        {
            id: 'nav-data-attributes',
            title: 'Go to Data Attributes',
            shortcut: '',
            icon: FileSpreadsheet,
            group: 'navigation',
            action: () => {
                navigate('/data-attributes');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // CATALOGS & ASSETS
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-tags',
            title: 'Go to Tags',
            shortcut: '',
            icon: Tag,
            group: 'navigation',
            action: () => {
                navigate('/tags');
                onClose(false);
            },
        },
        {
            id: 'nav-industries',
            title: 'Go to Industries',
            shortcut: '',
            icon: Factory,
            group: 'navigation',
            action: () => {
                navigate('/industries');
                onClose(false);
            },
        },
        {
            id: 'nav-visual-catalog',
            title: 'Go to Visual Catalog',
            shortcut: '',
            icon: Image,
            group: 'navigation',
            action: () => {
                navigate('/visual-catalog');
                onClose(false);
            },
        },
        {
            id: 'nav-artifacts',
            title: 'Go to Artifacts',
            shortcut: '',
            icon: Paperclip,
            group: 'navigation',
            action: () => {
                navigate('/artifacts');
                onClose(false);
            },
        },
        {
            id: 'nav-document-categories',
            title: 'Go to Document Categories',
            shortcut: '',
            icon: FolderOpen,
            group: 'navigation',
            action: () => {
                navigate('/document-categories');
                onClose(false);
            },
        },
        {
            id: 'nav-field-metadata',
            title: 'Go to Field Metadata',
            shortcut: '',
            icon: FormInput,
            group: 'navigation',
            action: () => {
                navigate('/field-metadata');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // SECURITY & ADMINISTRATION
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-security',
            title: 'Go to Security',
            shortcut: 'G S',
            icon: Shield,
            group: 'navigation',
            action: () => {
                navigate('/security');
                onClose(false);
            },
        },
        {
            id: 'nav-my-sessions',
            title: 'Go to My Sessions',
            shortcut: '',
            icon: Monitor,
            group: 'navigation',
            action: () => {
                navigate('/my-sessions');
                onClose(false);
            },
        },
        {
            id: 'nav-login-history',
            title: 'Go to Login History',
            shortcut: '',
            icon: History,
            group: 'navigation',
            action: () => {
                navigate('/login-history');
                onClose(false);
            },
        },
        {
            id: 'nav-security-alerts',
            title: 'Go to Security Alerts',
            shortcut: '',
            icon: Bell,
            group: 'navigation',
            action: () => {
                navigate('/security-alerts');
                onClose(false);
            },
        },
        {
            id: 'nav-compliance-reports',
            title: 'Go to Compliance Reports',
            shortcut: '',
            icon: ClipboardCheck,
            group: 'navigation',
            action: () => {
                navigate('/compliance-reports');
                onClose(false);
            },
        },
        {
            id: 'nav-settings',
            title: 'Go to Settings',
            shortcut: 'G ,',
            icon: Settings,
            group: 'navigation',
            action: () => {
                navigate('/system-settings');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // TOOLS & DIAGNOSTICS
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-diagnostics',
            title: 'Go to Diagnostics',
            shortcut: 'G D',
            icon: Activity,
            group: 'navigation',
            action: () => {
                navigate('/diagnostics');
                onClose(false);
            },
        },
        {
            id: 'nav-mcp-console',
            title: 'Go to MCP Console',
            shortcut: 'G M',
            icon: Terminal,
            group: 'navigation',
            action: () => {
                navigate('/mcp');
                onClose(false);
            },
        },
        {
            id: 'nav-apis',
            title: 'Go to APIs',
            shortcut: 'G A',
            icon: Code,
            group: 'navigation',
            action: () => {
                navigate('/apis');
                onClose(false);
            },
        },
        {
            id: 'nav-webhooks',
            title: 'Go to Webhooks',
            shortcut: '',
            icon: Webhook,
            group: 'navigation',
            action: () => {
                navigate('/webhooks');
                onClose(false);
            },
        },
        {
            id: 'nav-credentials',
            title: 'Go to Credentials',
            shortcut: '',
            icon: Key,
            group: 'navigation',
            action: () => {
                navigate('/credentials');
                onClose(false);
            },
        },
        {
            id: 'nav-email',
            title: 'Go to Email Management',
            shortcut: 'G E',
            icon: Mail,
            group: 'navigation',
            action: () => {
                navigate('/email');
                onClose(false);
            },
        },

        // ═══════════════════════════════════════════════════════════════
        // ADMIN - DATA QUALITY (PROJ-047)
        // ═══════════════════════════════════════════════════════════════
        {
            id: 'nav-admin-duplicates',
            title: 'Go to Duplicate Detection',
            shortcut: '',
            icon: Copy,
            group: 'navigation',
            action: () => {
                navigate('/admin/data-quality/duplicates');
                onClose(false);
            },
        },
        {
            id: 'nav-admin-merge-history',
            title: 'Go to Merge History',
            shortcut: '',
            icon: GitMerge,
            group: 'navigation',
            action: () => {
                navigate('/admin/merge-history');
                onClose(false);
            },
        },
    ];
}
